import { useIsMutating, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import { ChevronRight, FilePlus2, HelpCircle, MoreHorizontal, RefreshCw } from "lucide-react";
import { type ReactNode, useEffect, useRef, useState } from "react";

import { ApiError, api } from "../../api/client";
import type { Order, RotationSummary, WingoApplied } from "../../api/types";
import { ORDER_APPROVE } from "../../app/dossierState";
import { attentionSelector, designate, designateSoon } from "../../components/attention";
import { Dialog, DialogBody } from "../../components/Dialog";
import { Menu, MenuItem } from "../../components/Menu";
import { Presence } from "../../components/presence";
import { Badge, Button } from "../../components/ui";
import { useMediaQuery } from "../../components/useMediaQuery";
import { Waiting } from "../../components/Waiting";
import { rotationName } from "../../centacRotation";
import { t, tServer } from "../../i18n/i18n";
import { designateDossierReadOnly, useWriteBlock } from "../../sharing/access";
import { useUi } from "../../state/ui";
import { CENTAC_ROTATION_FIELD } from "../missionDataTarget";
import { showMissionField } from "../openMissionData";
import { ORDER_REOPEN } from "./attentionTargets";
import { FragoCreateDialog } from "./FragoCreate";
import { RegenerateDialog } from "./RotationDialogs";
import { RESULTS_FORM, resultsMutationKey } from "./ResultsView";
import {
  actionLabel,
  currentStep,
  itemKey,
  itemLabel,
  nextFrago,
  type Opened,
  rotationSteps,
  type Step,
  stepCount,
  stepAction,
  stepDetail,
  stepTitle,
  type TimelineItem,
  timelineItems,
  wingoNumber,
} from "./rotationSteps";
import { refreshAfterWingo } from "./WingoView";

/** Bouton de l'action attendue (`data-attention`) : désigné après une génération, une saisie… */
export const ROTATION_STEP = "rotation-step";

const TONE_DOT: Record<TimelineItem["tone"], string> = {
  fait: "bg-ok",
  en_cours: "bg-accent",
  a_faire: "border border-line-strong bg-surface",
};

/** Ouvre un document de la frise dans la vue de l'ordre. */
export function useOpenItem(): (item: Pick<Opened, "kind" | "opo" | "number">) => void {
  const setOpo = useUi((s) => s.setOpo);
  const setFrago = useUi((s) => s.setFrago);
  const setRotationPart = useUi((s) => s.setRotationPart);
  return (item) => {
    setOpo(item.opo);
    if (item.kind === "frago") setFrago(item.number);
    else if (item.kind === "results") setRotationPart("results");
    else if (item.kind === "wingo") setRotationPart("wingo");
    else {
      setFrago(null);
      setRotationPart(null);
    }
  };
}

/** Rotation CENTAC dans la vue de l'ordre (demande d'Alexis du 26/10/2026) : la frise de tous les documents de la
 *  rotation dans l'ordre chronologique, chacun avec sa pastille d'état, qui ouvre le document ; dessous, l'étape
 *  courante et sa seule action, qui pulse. Les actions secondaires (FRAGO, mise à jour depuis le WINGO, source des
 *  textes) sont rangées dans « Autres actions ».
 *
 *  `documentAction` : la commande du document ouvert quand l'étape le concerne (« Valider l'OPO N°… », « Rédiger
 *  l'OPO N°1 ») — celle de la vue de l'ordre, avec son contrôle de cohérence et sa signature. `holdPulse` : le
 *  document ouvert attend d'abord sa propre action (paragraphes à relire, signature à refaire, FRAGO à valider) ;
 *  l'étape reste affichée sans pulser. */
export function RotationTimeline({
  exerciseId,
  opened,
  order,
  documentAction,
  holdPulse = false,
  onRewritten,
}: {
  exerciseId: string;
  opened: Opened;
  /** OPO ouvert (pour la mise à jour depuis le WINGO), absent s'il n'est pas rédigé. */
  order?: Order;
  documentAction?: ReactNode;
  holdPulse?: boolean;
  onRewritten?: (order: Order) => void;
}) {
  const queryClient = useQueryClient();
  const notify = useUi((s) => s.notify);
  const openItem = useOpenItem();
  const writeBlock = useWriteBlock();
  const locked = writeBlock.blocked;
  const wide = useMediaQuery("(min-width: 1280px)");
  const [menuOpen, setMenuOpen] = useState(false);
  const [dialog, setDialog] = useState<null | "frago" | "regenerate" | "sources">(null);
  const rotationQuery = useQuery({ queryKey: ["rotation", exerciseId], queryFn: () => api.rotation(exerciseId) });
  const fragosQuery = useQuery({ queryKey: ["fragos", exerciseId], queryFn: () => api.fragos(exerciseId) });
  const savingResults = useIsMutating({ mutationKey: resultsMutationKey(exerciseId) }) > 0;
  const listRef = useRef<HTMLOListElement>(null);
  const summary: RotationSummary | undefined = rotationQuery.data;
  const openKey = itemKey(opened);
  // Document ouvert amené en vue dans la frise (tablette : la frise défile).
  useEffect(() => {
    const chip = listRef.current?.querySelector<HTMLElement>(`[data-item="${openKey}"]`);
    chip?.scrollIntoView?.({ block: "nearest", inline: "nearest" });
  }, [openKey, summary]);

  const generate = useMutation({
    mutationFn: (opo: number) => api.applyWingo(exerciseId, opo, { results: {}, contextual: {}, regenerate: true }),
    onSuccess: (result: WingoApplied, opo) => {
      refreshAfterWingo(queryClient, exerciseId, result);
      openItem({ kind: "wingo", opo, number: wingoNumber(opo) });
      if (result.notice) notify({ text: t("WINGO N°{n} généré", { n: wingoNumber(opo) }), detail: tServer(result.notice), tone: "warn" });
      else notify({ text: t("WINGO N°{n} généré : l'OPO N°{next} est rédigé", { n: wingoNumber(opo), next: opo + 1 }), detail: t("Relisez le WINGO, puis diffusez-le."), tone: "ok" });
    },
    onError: (err) => notify({ text: err instanceof ApiError ? err.message : t("Opération impossible"), tone: "error" }),
  });
  const diffuse = useMutation({
    mutationFn: (opo: number) => api.finalizeWingo(exerciseId, wingoNumber(opo)),
    onSuccess: (data, opo) => {
      queryClient.setQueryData(["wingo", exerciseId, data.number], data);
      refreshAfterWingo(queryClient, exerciseId);
      notify({ text: t("WINGO N°{n} diffusé", { n: data.number }), detail: t("L'OPO N°{next} est à relire et à valider.", { next: opo + 1 }), tone: "ok" });
      // L'étape suivante se fait sur l'OPO annoncé : il s'ouvre.
      openItem({ kind: "opo", opo: opo + 1, number: null });
    },
    onError: (err) => notify({ text: err instanceof ApiError ? err.message : t("Opération impossible"), tone: "error" }),
  });

  if (!summary) return null;
  const items = timelineItems(summary, fragosQuery.data);
  const steps = rotationSteps(summary);
  const current = currentStep(steps);
  const action = current ? stepAction(current, summary, opened) : null;
  // FRAGO rédigeable sur l'OPO ouvert (validé, aucun FRAGO en rédaction).
  const fragoNumber = nextFrago(fragosQuery.data, opened.opo);
  const openedRow = summary.opos[opened.opo - 1];
  const canUpdateFromWingo =
    opened.kind === "opo" && opened.opo > 1 && Boolean(order) && Boolean(openedRow?.announced);

  const run = (step: Step) => {
    if (!action) return;
    const n = step.opo;
    switch (action) {
      case "numero":
        showMissionField(CENTAC_ROTATION_FIELD);
        return;
      case "relire":
        openItem({ kind: "opo", opo: n, number: null });
        designateSoon(attentionSelector(ORDER_APPROVE), "auto", 2500);
        return;
      case "rediger":
        // Commande de la vue de l'ordre (génération), une fois l'OPO ouvert.
        openItem({ kind: "opo", opo: n, number: null });
        designateSoon(attentionSelector("order-generate"), "auto", 2500, (el) => {
          if (el && !(el as HTMLButtonElement).disabled) el.click();
        });
        return;
      case "mettre_a_jour":
        if (locked) return designateDossierReadOnly();
        openItem({ kind: "opo", opo: n, number: null });
        setDialog("regenerate");
        return;
      case "saisir":
        openItem({ kind: "results", opo: n, number: null });
        return;
      case "generer":
        if (locked) return designateDossierReadOnly();
        generate.mutate(n);
        return;
      case "relire_wingo":
        openItem({ kind: "wingo", opo: n, number: wingoNumber(n) });
        return;
      case "diffuser":
        if (locked) return designateDossierReadOnly();
        diffuse.mutate(n);
        return;
      default:
        return;
    }
  };

  const busy = generate.isPending || diffuse.isPending || savingResults;
  const pulse = !holdPulse && !locked && !busy;
  let primary: ReactNode = null;
  if (current && action) {
    const label = actionLabel(action, current);
    const navigation = action === "relire" || action === "relire_wingo" || action === "saisir" || action === "numero";
    if ((action === "valider" || action === "rediger") && documentAction && itemKey(opened) === `opo-${current.opo}`) {
      primary = documentAction;
    } else if (action === "enregistrer") {
      primary = (
        <Button size="sm" type="submit" form={RESULTS_FORM} data-attention={ROTATION_STEP} highlight={pulse} disabled={savingResults} {...writeBlock}>
          {savingResults ? <Waiting>{t("Enregistrement…")}</Waiting> : label}
        </Button>
      );
    } else {
      primary = (
        <Button
          size="sm"
          // Le document ouvert attend d'abord sa propre action (FRAGO à valider, relecture) : l'étape reste en
          // retrait, une seule action en avant.
          variant={holdPulse ? "secondary" : "primary"}
          data-attention={ROTATION_STEP}
          highlight={pulse}
          disabled={busy}
          onClick={() => run(current)}
          {...(navigation ? {} : writeBlock)}
        >
          {generate.isPending ? (
            <Waiting>{t("Génération du WINGO…")}</Waiting>
          ) : diffuse.isPending ? (
            <Waiting>{t("Diffusion…")}</Waiting>
          ) : (
            <>
              {label}
              {navigation && <ChevronRight size={14} aria-hidden />}
            </>
          )}
        </Button>
      );
    }
  }

  return (
    <section className="shrink-0 border-b border-line bg-surface" aria-label={t("Rotation CENTAC")} data-centac-timeline="">
      {/* Frise : tous les documents de la rotation, dans l'ordre ; le document ouvert est marqué. */}
      <nav
        aria-label={t("Documents de la rotation")}
        // Frise plus large que l'écran (tablette, FRAGO nombreux) : elle défile, ses bords s'estompent.
        className="overflow-x-auto px-4 [scrollbar-width:none] [mask-image:linear-gradient(to_right,transparent,#000_16px,#000_calc(100%-16px),transparent)]"
      >
        <ol ref={listRef} className="flex min-w-max items-center gap-0.5 py-1.5">
          {items.map((item, i) => {
            const active = item.key === openKey;
            const groupStart = item.kind === "opo" && i > 0;
            const label = itemLabel(item);
            const state = t(item.state);
            return (
              <li key={item.key} className="flex items-center">
                {groupStart && <span aria-hidden className="mx-1.5 h-4 w-px bg-line-strong" />}
                <button
                  type="button"
                  data-item={item.key}
                  aria-current={active ? "page" : undefined}
                  title={`${label} — ${state}`}
                  onClick={() => openItem(item)}
                  className={clsx(
                    "focus-inset flex min-h-8 items-center gap-1.5 rounded-md px-2 py-1.5 text-ui whitespace-nowrap transition-colors duration-(--gx-enter)",
                    active ? "bg-canvas font-semibold text-ink shadow-[inset_0_0_0_1px_var(--color-line-strong)]" : "text-ink-soft hover:bg-hover/60 hover:text-ink",
                  )}
                >
                  <span aria-hidden className={clsx("size-2 shrink-0 rounded-full", TONE_DOT[item.tone])} />
                  <span>{label}</span>
                  <span className="sr-only">— {state}</span>
                </button>
              </li>
            );
          })}
        </ol>
      </nav>
      {/* Étape courante et sa seule action. */}
      <div className="flex min-h-11 flex-wrap items-center gap-x-3 gap-y-1.5 border-t border-line bg-accent-soft/35 px-4 py-2">
        {summary.rotation && <span className="shrink-0 text-ui font-semibold text-ink">{rotationName(summary.rotation)}</span>}
        {current ? (
          <>
            {current.kind === "numero" ? (
              <Badge tone="accent">{t("Avant de commencer")}</Badge>
            ) : (
              <Badge tone="accent">{t("Étape {rank} sur {total}", { rank: current.rank, total: stepCount(steps) })}</Badge>
            )}
            <div className="min-w-0 flex-1">
              <p className="truncate text-ui font-semibold text-ink">{stepTitle(current)}</p>
              {wide && <p className="truncate text-meta text-ink-soft">{stepDetail(current)}</p>}
            </div>
          </>
        ) : (
          <>
            <Badge tone="ok">{t("Rotation terminée")}</Badge>
            <p className="min-w-0 flex-1 truncate text-ui text-ink-soft">
              {t("Les quatre OPO, leurs résultats et les trois WINGO sont établis.")}
            </p>
          </>
        )}
        <Menu
          open={menuOpen}
          onClose={() => setMenuOpen(false)}
          align="end"
          width={300}
          ariaLabel={t("Autres actions de la rotation")}
          trigger={
            <Button size="sm" variant="ghost" aria-expanded={menuOpen} onClick={() => setMenuOpen((o) => !o)} title={t("Autres actions de la rotation")}>
              <MoreHorizontal size={15} /> {wide && t("Autres actions")}
            </Button>
          }
        >
          {fragoNumber !== null && (
            <MenuItem
              icon={FilePlus2}
              label={t("Rédiger le FRAGO N°{n}", { n: fragoNumber })}
              hint={t("Ordre de conduite pendant l'exécution de l'OPO N°{n}", { n: opened.opo })}
              onSelect={() => setDialog("frago")}
              {...(locked ? { blocked: true, onBlocked: () => { setMenuOpen(false); window.requestAnimationFrame(designateDossierReadOnly); } } : {})}
            />
          )}
          {canUpdateFromWingo && (
            <MenuItem
              icon={RefreshCw}
              label={t("Mettre à jour l'OPO N°{n} depuis le WINGO N°{w}", { n: opened.opo, w: wingoNumber(opened.opo - 1) })}
              hint={t("Réécrit l'ennemi, l'appréciation, la mission et l'idée de manœuvre")}
              onSelect={() => setDialog("regenerate")}
              blocked={locked || order?.status === "finalized"}
              onBlocked={() => {
                setMenuOpen(false);
                if (locked) return window.requestAnimationFrame(designateDossierReadOnly);
                designateSoon(attentionSelector(ORDER_REOPEN));
                notify({ text: t("OPO validé : rouvrez-le d'abord"), tone: "info" });
              }}
            />
          )}
          <MenuItem icon={HelpCircle} label={t("D'où viennent les textes des ordres ?")} onSelect={() => setDialog("sources")} />
        </Menu>
        {primary}
      </div>

      <Presence show={dialog === "frago" && fragoNumber !== null}>
        {dialog === "frago" && fragoNumber !== null && (
          <FragoCreateDialog
            exerciseId={exerciseId}
            opo={opened.opo}
            next={fragoNumber}
            onClose={() => setDialog(null)}
            onCreated={(created) => {
              setDialog(null);
              openItem({ kind: "frago", opo: opened.opo, number: created.number });
            }}
          />
        )}
      </Presence>
      <Presence show={dialog === "regenerate"}>
        {dialog === "regenerate" && (
          <RegenerateDialog
            exerciseId={exerciseId}
            number={opened.opo}
            onClose={() => setDialog(null)}
            onDone={(o) => {
              setDialog(null);
              void queryClient.invalidateQueries({ queryKey: ["rotation", exerciseId] });
              void queryClient.invalidateQueries({ queryKey: ["exercises"] });
              onRewritten?.(o);
            }}
          />
        )}
      </Presence>
      <Presence show={dialog === "sources"}>{dialog === "sources" && <SourcesDialog onClose={() => setDialog(null)} />}</Presence>
    </section>
  );
}

/** « D'où viennent les textes des ordres ? » : les quatre sources, en mots simples (l'éditeur les indique sous le
 *  titre de chaque paragraphe). */
function SourcesDialog({ onClose }: { onClose: () => void }) {
  const rows: [string, string][] = [
    [t("Repris de la rotation type du CENTAC"), t("La rotation de Mailly, toujours la même, fixe pour chaque OPO la mission du GTIA et celles de l'EDC (SGTIA A) et de l'EEI (SGTIA B) à chaque phase, la matrice du § 3.b, le soutien du TC2 et l'animation de la FORAD.")],
    [t("Tiré de la carte"), t("Positions des unités et lieux cités, LIMA et leurs heures, ennemi de la FORAD, PC et TC2.")],
    [t("Saisi par vous"), t("Numéro de la rotation, date de début et fuseau (Données initiales), résultats de chaque phase, contenu du WINGO, retouches dans l'éditeur.")],
    [t("Mis à jour depuis le WINGO"), t("Pour les OPO N°2 à 4 : situation de l'ennemi, appréciation du chef, mission, idée de manœuvre et bascules de la matrice, d'après les résultats saisis.")],
  ];
  return (
    <Dialog title={t("D'où viennent les textes des ordres ?")} onClose={onClose} width={560}>
      <DialogBody className="text-ui">
        <ul className="space-y-2">
          {rows.map(([head, text]) => (
            <li key={head} className="leading-snug text-ink-soft">
              <span className="font-semibold text-ink">{head}.</span> {text}
            </li>
          ))}
        </ul>
        <p className="text-meta text-ink-muted">{t("L'éditeur de l'ordre indique la source sous le titre de chaque paragraphe.")}</p>
      </DialogBody>
    </Dialog>
  );
}

/** Désigne l'action attendue de la rotation (après une écriture qui la change). */
export function designateStep(): void {
  designate(attentionSelector(ROTATION_STEP));
}
